import React, { useState, useEffect, useRef } from 'react';
import { DataManager } from '../../services/dataManager';

const BreathingExercise = ({ client }) => {
    // Modes: 'classic', 'square', 'antistress'
    const [mode, setMode] = useState('classic');
    const [isActive, setIsActive] = useState(false);
    const [phase, setPhase] = useState('idle'); // idle, inhale, holdIn, exhale, holdOut
    const [instruction, setInstruction] = useState('Iniziamo');

    // Cycle/Duration tracking
    const [cycleCount, setCycleCount] = useState(0); // Current cycle count
    const [targetCycles, setTargetCycles] = useState(10); // Target number of cycles
    const [durationType, setDurationType] = useState('cycles'); // 'cycles' or 'time'
    const [targetMinutes, setTargetMinutes] = useState(3); // Target minutes if using time
    const [startTime, setStartTime] = useState(null);

    // Configuration for each mode
    const [config, setConfig] = useState({
        classic: { inhale: 5, holdIn: 0, exhale: 5, holdOut: 0, name: 'Respira' },
        square: { inhale: 4, holdIn: 4, exhale: 4, holdOut: 4, name: 'Respirazione Quadrata' },
        antistress: { inhale: 4, holdIn: 7, exhale: 8, holdOut: 0, name: 'Antistress (4-7-8)' }
    });

    // Audio Context Ref
    const audioContextRef = useRef(null);
    const oscillatorRef = useRef(null);
    const gainNodeRef = useRef(null);

    // Timer Ref
    const timeoutRef = useRef(null);

    // Initialize Audio Context on user interaction (start)
    const initAudio = () => {
        if (!audioContextRef.current) {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            audioContextRef.current = new AudioContext();
        }
        if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }
    };

    const stopSound = () => {
        if (oscillatorRef.current) {
            try {
                const now = audioContextRef.current.currentTime;
                gainNodeRef.current.gain.cancelScheduledValues(now);
                gainNodeRef.current.gain.setValueAtTime(gainNodeRef.current.gain.value, now);
                gainNodeRef.current.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
                oscillatorRef.current.stop(now + 0.1);
            } catch (e) { console.error(e) }
            oscillatorRef.current = null;
        }
    };

    const playSound = (type, duration) => {
        if (!audioContextRef.current) return;
        stopSound(); // Ensure previous sound is stopped

        const ctx = audioContextRef.current;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.connect(gain);
        gain.connect(ctx.destination);

        const now = ctx.currentTime;
        const volume = 0.1; // Low volume background

        if (type === 'inhale') {
            osc.type = 'sine';
            osc.frequency.setValueAtTime(200, now);
            osc.frequency.exponentialRampToValueAtTime(400, now + duration);

            gain.gain.setValueAtTime(0.001, now);
            gain.gain.linearRampToValueAtTime(volume, now + (duration * 0.2)); // Fade in
            gain.gain.setValueAtTime(volume, now + (duration * 0.8)); // Sustain
            gain.gain.linearRampToValueAtTime(0.001, now + duration); // Fade out
        } else if (type === 'exhale') {
            osc.type = 'sine';
            osc.frequency.setValueAtTime(400, now);
            osc.frequency.exponentialRampToValueAtTime(200, now + duration);

            gain.gain.setValueAtTime(0.001, now);
            gain.gain.linearRampToValueAtTime(volume, now + (duration * 0.2));
            gain.gain.setValueAtTime(volume, now + (duration * 0.8));
            gain.gain.linearRampToValueAtTime(0.001, now + duration);
        }

        osc.start(now);
        osc.stop(now + duration + 0.1);

        oscillatorRef.current = osc;
        gainNodeRef.current = gain;
    };

    useEffect(() => {
        return () => {
            if (isActive) stopSound();
            clearTimeout(timeoutRef.current);
        };
    }, []);

    // Cycle/Duration tracking (Ref for mutable access in closure)
    const cycleCountRef = useRef(0);

    useEffect(() => {
        if (isActive) {
            setStartTime(Date.now());
            setCycleCount(0);
            cycleCountRef.current = 0;
            runPhase('inhale');
        } else {
            clearTimeout(timeoutRef.current);
            setPhase('idle');
            setInstruction('Iniziamo');
            stopSound();
            setStartTime(null);
        }
    }, [isActive]);

    const handleSessionComplete = async () => {
        if (!client) return;

        try {
            await DataManager.saveWork({
                clientId: client.id,
                type: 'breathing_session',
                content: {
                    mode: config[mode].name,
                    durationFormatted: durationType === 'cycles'
                        ? `${cycleCountRef.current} cicli`
                        : `${Math.floor((Date.now() - startTime) / 1000 / 60)} minuti`,
                    target: durationType === 'cycles' ? `${targetCycles} cicli` : `${targetMinutes} min`
                },
                shared: true,
                timestamp: new Date().toISOString()
            });
            // Optional: alert or toast? Maybe too intrusive. 
            // console.log("Session saved");
        } catch (e) {
            console.error("Error saving breathing session:", e);
        }
    };

    const runPhase = (currentPhase) => {
        // Use a ref to access the latest config even inside the closure
        // (Note: if config/mode changes, isActive becomes false, so this acts safely)
        const settings = config[mode];
        let duration = 0;
        let nextPhase = '';
        let text = '';
        let soundType = null;

        switch (currentPhase) {
            case 'inhale':
                duration = settings.inhale;
                nextPhase = settings.holdIn > 0 ? 'holdIn' : 'exhale';
                text = 'Inspira';
                soundType = 'inhale';
                break;
            case 'holdIn':
                duration = settings.holdIn;
                nextPhase = 'exhale';
                text = 'Trattieni';
                soundType = null; // Silence
                break;
            case 'exhale':
                duration = settings.exhale;
                nextPhase = settings.holdOut > 0 ? 'holdOut' : 'inhale';
                text = 'Espira';
                soundType = 'exhale';
                break;
            case 'holdOut':
                duration = settings.holdOut;
                nextPhase = 'inhale';
                text = 'Attendi';
                soundType = null; // Silence
                break;
            default:
                return;
        }

        setPhase(currentPhase);
        setInstruction(text);
        if (soundType) playSound(soundType, duration);
        else stopSound();

        timeoutRef.current = setTimeout(() => {
            if (isActive) {
                // Check if we should stop based on cycles or time
                if (nextPhase === 'inhale') {
                    // Increment via Ref to avoid stale closure
                    cycleCountRef.current += 1;
                    const currentCycles = cycleCountRef.current;
                    setCycleCount(currentCycles);

                    // Check completion
                    if (durationType === 'cycles' && currentCycles >= targetCycles) {
                        handleSessionComplete();
                        setIsActive(false);
                        return;
                    } else if (durationType === 'time' && startTime) {
                        const elapsed = (Date.now() - startTime) / 1000 / 60; // minutes
                        if (elapsed >= targetMinutes) {
                            handleSessionComplete();
                            setIsActive(false);
                            return;
                        }
                    }
                }
                runPhase(nextPhase);
            }
        }, duration * 1000);
    };

    const handleConfigChange = (key, value) => {
        setConfig(prev => ({
            ...prev,
            [mode]: {
                ...prev[mode],
                [key]: parseFloat(value)
            }
        }));
    };

    // For square breathing, update all 4 phases at once
    const handleSquareChange = (value) => {
        const parsed = parseFloat(value);
        setConfig(prev => ({
            ...prev,
            square: {
                ...prev.square,
                inhale: parsed,
                holdIn: parsed,
                exhale: parsed,
                holdOut: parsed
            }
        }));
    };

    const toggleSession = () => {
        if (!isActive) initAudio();
        setIsActive(!isActive);
    };

    const currentSettings = config[mode];

    return (
        <div className="flex flex-col items-center py-6 px-4 bg-white rounded-3xl shadow-sm border border-gray-100 min-h-[600px] w-full max-w-2xl mx-auto">

            {/* Header / Mode Selection */}
            <div className="w-full flex justify-center mb-8">
                <div className="bg-slate-100 p-1 rounded-xl flex gap-1">
                    {['classic', 'square', 'antistress'].map((m) => (
                        <button
                            key={m}
                            onClick={() => { setIsActive(false); setMode(m); }}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all font-sans ${mode === m
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            {config[m].name.split(' (')[0].replace('Respirazione ', '')}
                        </button>
                    ))}
                </div>
            </div>

            <div className="text-center mb-6 h-16">
                <h2 className="text-2xl font-bold text-gray-800 transition-all font-sans">{config[mode].name}</h2>
                <p className="text-sm text-gray-500 font-sans">
                    {isActive
                        ? `${durationType === 'cycles' ? `Ciclo ${cycleCount}/${targetCycles}` : `${Math.floor((Date.now() - startTime) / 1000 / 60)}min / ${targetMinutes}min`}`
                        : "Configura e inizia quando vuoi."}
                </p>
            </div>

            {/* Animation Container */}
            <div className="relative w-80 h-80 flex items-center justify-center mb-10">

                {/* Outer Ring */}
                <div className="absolute w-full h-full rounded-full border border-slate-100"></div>
                <div className="absolute w-3/4 h-3/4 rounded-full border border-slate-100"></div>

                {/* Cloud/Ball */}
                <div
                    className={`
                        absolute rounded-full shadow-sm flex items-center justify-center text-white
                        transition-all ease-linear z-10
                    `}
                    style={{
                        width: '12rem', // Base size (w-48)
                        height: '12rem',
                        transitionDuration: `${isActive
                            ? (phase === 'inhale' ? config[mode].inhale
                                : phase === 'exhale' ? config[mode].exhale
                                    : phase === 'holdIn' || phase === 'holdOut' ? 0
                                        : 0.5)
                            : 0.5}s`,
                        transform: phase === 'inhale' || phase === 'holdIn' ? 'scale(1.5)' : 'scale(0.8)',
                        backgroundColor: phase === 'holdIn' || phase === 'holdOut'
                            ? 'rgba(148, 163, 184, 0.3)' // slate-400 with transparency for Hold
                            : 'rgba(100, 116, 139, 0.3)', // slate-500 with transparency for Move
                        opacity: phase === 'holdOut' ? 0.5 : 0.7
                    }}
                >
                </div>

                {/* Text Indicator */}
                <div className={`absolute z-20 text-slate-500 drop-shadow-sm pointer-events-none transition-opacity duration-500 font-sans ${!isActive ? 'text-xl' : 'text-3xl'}`}>
                    {instruction}
                </div>
            </div>

            {/* Config Controls (only visible when not active) */}
            <div className={`w-full max-w-md space-y-4 mb-8 transition-all duration-500 ${isActive ? 'opacity-0 pointer-events-none h-0' : 'opacity-100 h-auto'}`}>

                {/* Duration Type Selector */}
                <div className="mb-4">
                    <label className="block text-xs font-bold text-slate-700 mb-2 font-sans">Durata Sessione</label>
                    <div className="flex gap-2 bg-slate-100 p-1 rounded-lg">
                        <button
                            onClick={() => setDurationType('cycles')}
                            className={`flex-1 py-2 px-4 rounded-lg text-sm font-bold transition font-sans ${durationType === 'cycles' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
                                }`}
                        >
                            Cicli
                        </button>
                        <button
                            onClick={() => setDurationType('time')}
                            className={`flex-1 py-2 px-4 rounded-lg text-sm font-bold transition font-sans ${durationType === 'time' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
                                }`}
                        >
                            Minuti
                        </button>
                    </div>
                </div>

                {/* Target Cycles or Minutes */}
                <div className="mb-4">
                    {durationType === 'cycles' ? (
                        <div>
                            <label className="block text-xs font-bold text-slate-700 mb-2 font-sans">Numero di cicli</label>
                            <input
                                type="number" min="1" max="50"
                                value={targetCycles}
                                onChange={(e) => setTargetCycles(parseInt(e.target.value) || 10)}
                                className="w-full p-3 bg-slate-50 rounded-lg border border-slate-200 text-center font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-800 font-sans"
                            />
                        </div>
                    ) : (
                        <div>
                            <label className="block text-xs font-bold text-slate-700 mb-2 font-sans">Durata in minuti</label>
                            <div className="flex gap-2">
                                {[1, 3, 5, 10].map(min => (
                                    <button
                                        key={min}
                                        onClick={() => setTargetMinutes(min)}
                                        className={`flex-1 py-2 px-3 rounded-lg font-bold text-sm transition font-sans ${targetMinutes === min
                                            ? 'bg-slate-800 text-white'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            }`}
                                    >
                                        {min}min
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sliders based on mode */}
                {mode === 'square' ? (
                    // Square breathing: single control for all 4 phases
                    <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1 font-sans">Durata (sec) - tutti i lati</label>
                        <input
                            type="number" min="1" max="10"
                            value={currentSettings.inhale}
                            onChange={(e) => handleSquareChange(e.target.value)}
                            className="w-full p-3 bg-slate-50 rounded-lg border border-slate-200 text-center font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-800 font-sans"
                        />
                        <p className="text-xs text-slate-500 mt-2 font-sans text-center">Inspiro, Apnea, Espiro, Apnea: {currentSettings.inhale}s ciascuno</p>
                    </div>
                ) : (
                    // Classic and Antistress: separate controls
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1 font-sans">Inspiro (sec)</label>
                            <input
                                type="number" min="1" max="10"
                                value={currentSettings.inhale}
                                onChange={(e) => handleConfigChange('inhale', e.target.value)}
                                className="w-full p-2 bg-slate-50 rounded-lg border border-slate-200 text-center font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-800 font-sans"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1 font-sans">Espiro (sec)</label>
                            <input
                                type="number" min="1" max="10"
                                value={currentSettings.exhale}
                                onChange={(e) => handleConfigChange('exhale', e.target.value)}
                                className="w-full p-2 bg-slate-50 rounded-lg border border-slate-200 text-center font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-800 font-sans"
                            />
                        </div>
                        {mode === 'antistress' && (
                            <div className="col-span-2">
                                <label className="block text-xs font-bold text-slate-600 mb-1 font-sans">Apnea Piena (sec)</label>
                                <input
                                    type="number" min="0" max="10"
                                    value={currentSettings.holdIn}
                                    onChange={(e) => handleConfigChange('holdIn', e.target.value)}
                                    className="w-full p-2 bg-slate-50 rounded-lg border border-slate-200 text-center font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-800 font-sans"
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Big Start Button */}
            <div className="flex gap-4">
                <button
                    onClick={toggleSession}
                    className={`
                        px-10 py-4 rounded-xl font-bold text-xl transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3 font-sans border-0
                        ${isActive
                            ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            : 'text-white hover:opacity-90'}
                    `}
                    style={!isActive ? { backgroundColor: '#24485d' } : {}}
                >
                    {isActive ? (
                        <><span>⏹️</span> Termina</>
                    ) : (
                        <><span>▶️</span> Inizia Sessione</>
                    )}
                </button>
            </div>
        </div>
    );
};

export default BreathingExercise;
